import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Heading,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import type { ModuleUpgradeOffer } from '@/common/api/models/moduleUpgradeOffer';
import type { ModuleUpgradePreviewGroup } from '@/common/api/models/moduleUpgradePreviewGroup';
import { ModuleUpgradeOfferModal } from '@/student/module/components/ModuleUpgradeOfferModal';
import {
  formatModuleVersionLabel,
  getUpgradeOfferTitleKey,
} from '@/student/module/components/moduleUpgradeOfferUtils';
import { compareModuleVersions } from '../utils/moduleVersionNumbers';

const PREVIEW_NS = 'lecturer.moduleEditor.moduleEditor.dialogs.requestApprovalOptions';

/** Same width as the student upgrade dialog (`ModuleUpgradeOfferBanner`). */
export const STUDENT_DIALOG_MAX_WIDTH = 420;

/**
 * The card lays out at the student's real width, then shrinks as a thumbnail.
 * Layout ignores transforms, so the wrapper reserves the scaled height itself.
 */
export const PREVIEW_SCALE = 0.85;

const SCALED_CARD_STYLE = {
  transform: [{ scale: PREVIEW_SCALE }],
  transformOrigin: 'top left',
} as const;

const PLACEHOLDER_VERSION = 'v**';

export type ModuleUpgradeOfferPreviewPanelProps = {
  draftVersion: string;
  groups: ModuleUpgradePreviewGroup[];
  impactNotice: string;
};

const latestVersionOf = (group: ModuleUpgradePreviewGroup): string => (
  (group.versions ?? []).reduce<string>(
    (latest, version) => (!latest || compareModuleVersions(version, latest) > 0 ? version : latest),
    '',
  )
);

/** The group holding the newest older published version. */
export const pickLatestPreviewGroup = (
  groups: ModuleUpgradePreviewGroup[],
): ModuleUpgradePreviewGroup | undefined => groups.reduce<ModuleUpgradePreviewGroup | undefined>(
  (latest, group) => {
    if ((group.versions ?? []).length === 0) return latest;
    if (!latest) return group;
    return compareModuleVersions(latestVersionOf(group), latestVersionOf(latest)) > 0 ? group : latest;
  },
  undefined,
);

/**
 * ModuleUpgradeOfferPreviewPanel
 * ----
 * Inert student upgrade dialog for the lecturer approval request flow, at the
 * student dialog's own width, for students on the latest older version.
 */
export function ModuleUpgradeOfferPreviewPanel({
  draftVersion,
  groups,
  impactNotice,
}: ModuleUpgradeOfferPreviewPanelProps) {
  const { t } = useTranslation();

  const latestGroup = useMemo(() => pickLatestPreviewGroup(groups), [groups]);
  const [cardHeight, setCardHeight] = useState(0);

  const previewOffer: ModuleUpgradeOffer | null = useMemo(() => {
    if (!latestGroup) {
      return null;
    }
    return {
      targetVersion: draftVersion,
      pinnedVersion: latestVersionOf(latestGroup),
      headline: '',
      impactNotice: impactNotice.trim(),
      changeSummary: latestGroup.changeSummary ?? undefined,
      removedTasks: [],
      addedTasks: [],
    };
  }, [draftVersion, impactNotice, latestGroup]);

  if (!previewOffer) {
    return null;
  }

  const modalTitleKey = getUpgradeOfferTitleKey(previewOffer.changeSummary);
  const versionLabel = t('student.module.upgrade-offer.version-label', {
    from: PLACEHOLDER_VERSION,
    to: formatModuleVersionLabel(previewOffer.targetVersion),
  });

  return (
    <VStack
      space="sm"
      bg="$bgSubtle"
      borderRadius="$lg"
      p="$4"
      alignItems="center"
      testID="module-upgrade-offer-preview-panel"
    >
      <Text
        size="xs"
        fontWeight="bold"
        color="$textSubtle"
        textTransform="uppercase"
        alignSelf="flex-start"
      >
        {t(`${PREVIEW_NS}.previewHeading`, { defaultValue: 'What students see' })}
      </Text>

      <Box
        w={STUDENT_DIALOG_MAX_WIDTH * PREVIEW_SCALE}
        h={cardHeight ? cardHeight * PREVIEW_SCALE : undefined}
        overflow="hidden"
      >
        <Box
          w={STUDENT_DIALOG_MAX_WIDTH}
          borderWidth={1}
          borderColor="$borderLight"
          borderRadius="$lg"
          bg="$bgCard"
          overflow="hidden"
          style={SCALED_CARD_STYLE}
          onLayout={(event) => setCardHeight(event.nativeEvent.layout.height)}
          testID="module-upgrade-offer-preview-dialog"
        >
          <Box px="$4" pt="$4" pb="$2">
            <Heading size="lg" testID="module-upgrade-offer-modal-title">
              {t(modalTitleKey)}
            </Heading>
            <Text size="sm" color="$textSubtle" testID="module-upgrade-offer-preview-version">
              {versionLabel}
            </Text>
          </Box>
          <ModuleUpgradeOfferModal
            courseId="preview"
            moduleId="preview"
            upgradeOffer={previewOffer}
            onClose={() => undefined}
            previewMode
          />
        </Box>
      </Box>
    </VStack>
  );
}
